import React, { useMemo, useState } from 'react';
import {
  MultiValueProps,
  OnChangeValue,
  Options,
  OptionsOrGroups,
} from 'react-select';
import { useQuery } from '@apollo/client';
import { useDebounce } from '../../utils/hooks';
import {
  ICreatableReactSelectOption,
  IReactSelectOption,
} from '../../utils/types';
import {
  IEntityCategoryFieldsFragment,
  IGetEntityCategoriesQuery,
  IGetEntityCategoriesQueryVariables,
} from '../../utils/apollo/graphqlSchema';
import { GET_ENTITY_CATEGORIES } from '../../utils/apollo/entityCategories';
import { CreatableReactSelectMenu } from '../Dropdown/StyledReactSelect';
import { CATEGORY_NAME_MAX_LENGTH } from '../../config/constants';
import Category from './Category';
import { defaultManagedOption } from './utils';

interface IProps {
  schemaId: string;
  selectedValues: ICreatableReactSelectOption[];
  onChange: (values: ICreatableReactSelectOption[]) => void;
}

const fallbackData: IEntityCategoryFieldsFragment[] = [];

const ApplyTagsSelectMultiValue = (
  props: MultiValueProps<IReactSelectOption, true, never>,
) => {
  const { children, removeProps } = props;

  return (
    <Category
      onClick={removeProps.onClick}
      showClearAction
      isManaged={props.data.value === defaultManagedOption.value}
    >
      {children}
    </Category>
  );
};

const CategoriesMenu = ({ schemaId, selectedValues, onChange }: IProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const { data, loading } = useQuery<
    IGetEntityCategoriesQuery,
    IGetEntityCategoriesQueryVariables
  >(GET_ENTITY_CATEGORIES, {
    variables: {
      schemaIds: [schemaId],
      term: debouncedSearchQuery,
      includes: [],
      pagination: {
        first: 50,
        after: 0,
      },
    },
  });

  const entities = data?.entityCategories?.entities ?? fallbackData;

  const handleChange = (
    newValue: OnChangeValue<ICreatableReactSelectOption, true>,
  ) => {
    onChange(newValue?.length ? [...newValue] : []);
  };

  const options: ICreatableReactSelectOption[] = useMemo(() => {
    return [
      {
        label: `* ${defaultManagedOption.label}`,
        value: defaultManagedOption.value,
      },
      ...entities.map((category) => ({
        label: category.name,
        value: category.id,
      })),
    ];
  }, [entities]);

  const isValidNewOption = (
    inputValue: string,
    currentValue: Options<ICreatableReactSelectOption>,
    menuOptions: OptionsOrGroups<ICreatableReactSelectOption, never>,
  ) => {
    if (!inputValue?.length) {
      return false;
    }

    // Check length
    if (inputValue.length > CATEGORY_NAME_MAX_LENGTH) {
      return false;
    }

    // Check menu value
    if (
      currentValue.some(
        (option) => option.label.toLowerCase() === inputValue.toLowerCase(),
      )
    ) {
      return false;
    }

    // Check options
    if (
      menuOptions?.length &&
      menuOptions.some(
        (option) => option.label.toLowerCase() === inputValue.toLowerCase(),
      )
    ) {
      return false;
    }

    return true;
  };

  return (
    <CreatableReactSelectMenu
      options={options}
      onChange={handleChange}
      value={selectedValues}
      isMulti={true}
      isSearchable={true}
      isLoading={loading}
      inputValue={searchQuery}
      onInputChange={setSearchQuery}
      placeholder="Search Tags"
      components={{
        MultiValue: ApplyTagsSelectMultiValue,
      }}
      formatCreateLabel={(inputValue: string) => (
        <div>
          <b>Apply Tags: </b>
          <span>{inputValue}</span>
        </div>
      )}
      isValidNewOption={isValidNewOption}
      blurInputOnSelect={true}
    />
  );
};

export default CategoriesMenu;
